// ESC/POS receipt encoder — pure, no hardware access.
//
// Turns a bill into the raw bytes an 80mm thermal printer understands
// (headers, item rows, totals, paper cut, cash-drawer kick). The transport
// that actually sends these bytes to a printer lives in thermalPrinter.js.
//
// Text is Latin/ASCII only: standard thermal printers can't render Ge'ez
// (Amharic) without special fonts, so receipts print in English + numbers.
// Any non-ASCII character is replaced with "?" so a stray character can
// never corrupt the byte stream.

const ESC = 0x1b, GS = 0x1d, LF = 0x0a;

class Encoder {
  constructor() { this.b = []; }
  raw(...bytes) { for (const x of bytes) { if (Array.isArray(x)) this.b.push(...x); else this.b.push(x); } return this; }
  init() { return this.raw(ESC, 0x40); }                       // ESC @  (reset)
  align(a) { return this.raw(ESC, 0x61, a === "center" ? 1 : a === "right" ? 2 : 0); }
  bold(on) { return this.raw(ESC, 0x45, on ? 1 : 0); }
  // width/height each 1..8 (1 = normal). GS ! n
  size(w, h) {
    const wi = Math.max(1, Math.min(8, w || 1)) - 1;
    const he = Math.max(1, Math.min(8, h || 1)) - 1;
    return this.raw(GS, 0x21, (wi << 4) | he);
  }
  text(s) {
    const clean = String(s == null ? "" : s).replace(/[^\x09\x0a\x20-\x7e]/g, "?");
    this.b.push(...new TextEncoder().encode(clean));
    return this;
  }
  line(s) { if (s != null) this.text(s); return this.raw(LF); }
  feed(n) { for (let i = 0; i < (n || 1); i++) this.raw(LF); return this; }
  hr(cols) { return this.line("-".repeat(cols || 42)); }
  // Left label + right-aligned value within `cols` characters. Wraps if the
  // two together don't fit on one line.
  row(left, right, cols) {
    cols = cols || 42;
    left = String(left == null ? "" : left);
    right = String(right == null ? "" : right);
    if (left.length + right.length + 1 > cols) {
      this.line(left);
      return this.line(" ".repeat(Math.max(0, cols - right.length)) + right);
    }
    return this.line(left + " ".repeat(Math.max(1, cols - left.length - right.length)) + right);
  }
  cut() { return this.raw(GS, 0x56, 0x42, 0x00); }             // GS V B 0  (partial cut + feed)
  drawer() { return this.raw(ESC, 0x70, 0x00, 0x19, 0xfa); }   // ESC p 0 25 250  (kick pin 2)
  // Native ESC/POS QR code (GS ( k, model 2). `mod` is the module size (1..16).
  // Used to print a scannable fiscal QR on the thermal slip.
  qr(data, mod = 6, ec = 0x31 /* L=0x30,M=0x31,Q=0x32,H=0x33 */) {
    const d = new TextEncoder().encode(String(data == null ? "" : data));
    this.raw(GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00);          // select model 2
    this.raw(GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, Math.max(1, Math.min(16, mod))); // module size
    this.raw(GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, ec);                  // error correction
    const len = d.length + 3;
    this.raw(GS, 0x28, 0x6b, len & 0xff, (len >> 8) & 0xff, 0x31, 0x50, 0x30); // store data
    this.b.push(...d);
    this.raw(GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30);                // print
    return this;
  }
  // Wrap a long string across `cols` characters (e.g. the IRN).
  wrap(s, cols) {
    s = String(s == null ? "" : s); cols = cols || 42;
    for (let i = 0; i < s.length; i += cols) this.line(s.slice(i, i + cols));
    return this;
  }
  bytes() { return new Uint8Array(this.b); }
}

function money(v, cur) {
  const n = Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return cur ? cur + " " + n : n;
}

// receipt = {
//   hotel: { legal_name|name, address, phone, tin, vat_reg_no },
//   doc_title, invoice_no, datetime,
//   lines: [{ description, qty, amount }],
//   subtotal_net, discount, tax_label, tax_amount, total,
//   method, tendered, change, reference, footer,
//   fiscal: { irn, rrn, system_number, qr_data, provider }  // optional — when
//           present the slip prints as a registered fiscal invoice (IRN + QR).
// }
export function buildReceipt(r, { cols = 42, cur = "ETB", openDrawer = true, cut = true } = {}) {
  const e = new Encoder();
  const h = r.hotel || {};
  e.init().align("center");
  e.bold(true).size(2, 2).line(h.legal_name || h.name || "").size(1, 1).bold(false);
  if (h.address) e.line(h.address);
  if (h.phone) e.line(h.phone);
  const idl = [h.tin ? "TIN: " + h.tin : "", h.vat_reg_no ? "VAT: " + h.vat_reg_no : ""].filter(Boolean).join("   ");
  if (idl) e.line(idl);
  e.feed(1);
  if (r.doc_title) e.bold(true).line(r.doc_title).bold(false);
  const meta = [r.invoice_no ? "No. " + r.invoice_no : "", r.datetime || ""].filter(Boolean).join("   ");
  if (meta) e.line(meta);
  e.align("left").hr(cols);
  (r.lines || []).forEach((l) => {
    const q = l.qty != null ? l.qty + " x " : "";
    e.row(q + (l.description || ""), money(l.amount, ""), cols);
  });
  e.hr(cols);
  if (r.discount > 0) e.row("Discount", "-" + money(r.discount, ""), cols);
  if (r.tax_amount > 0) {
    e.row("Subtotal", money(r.subtotal_net, ""), cols);
    e.row(r.tax_label || "Tax", money(r.tax_amount, ""), cols);
  }
  e.bold(true).size(1, 2).row("TOTAL", money(r.total, cur), cols).size(1, 1).bold(false);
  e.hr(cols);
  if (r.method) e.row("Paid (" + r.method + ")", money(r.paid != null ? r.paid : r.total, ""), cols);
  if (r.tendered != null) e.row("Tendered", money(r.tendered, ""), cols);
  if (r.change != null && r.change > 0) e.row("Change", money(r.change, ""), cols);
  if (r.reference) e.line("Ref: " + r.reference);

  // Fiscal block — a registered e-invoice (IRN + scannable QR) when present,
  // otherwise a clear "proforma / not a fiscal receipt" notice.
  const f = r.fiscal;
  if (f && f.irn) {
    e.hr(cols).align("center");
    e.bold(true).line("E-INVOICE REGISTERED (MoR EIMS)" + (f.provider === "mock" ? " [TEST]" : "")).bold(false);
    if (f.system_number) e.line("System No: " + f.system_number);
    e.align("left").line("IRN:").wrap(f.irn, cols);
    if (f.rrn) e.line("RRN: " + f.rrn);
    e.align("center").feed(1).qr(f.qr_data || f.irn, 6).feed(1);
    e.line("Scan the QR to verify this receipt.");
  } else {
    e.hr(cols).align("center");
    e.bold(true).line(r.footer || "PROFORMA - not a fiscal receipt").bold(false);
    e.line("A fiscal IRN is registered when settled.");
  }

  e.feed(1).align("center");
  if (f && f.irn && r.footer) e.line(r.footer);
  e.feed(1);
  if (cut) e.cut();
  if (openDrawer) e.drawer();
  return e.bytes();
}

// Standalone drawer-kick (e.g. a "no sale" open-drawer button).
export function drawerKickBytes() {
  return new Encoder().init().drawer().bytes();
}
